/**
 * Inline Issue Report Modal Component
 *
 * Auto-populates issue creation form with context from test step.
 * User only needs to provide severity and actual behavior.
 *
 * Module: 3.0 - System Operations (Issues Manager Phase 2)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.1
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, getAuthHeaders } from "@/lib/queryClient";
import { extractKeywords, COMPONENT_TO_MODULE_MAP } from "@/lib/constants/testing-constants";
import { ScreenshotUploadField } from "@/components/testing/screenshot-upload-field";
import type { TestingChecklistItem, TestingChecklist } from "@shared/schema";

interface InlineIssueReportModalProps {
  checklistItem: TestingChecklistItem | null;
  checklist: TestingChecklist | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InlineIssueReportModal({
  checklistItem,
  checklist,
  open,
  onOpenChange,
}: InlineIssueReportModalProps) {
  const [severity, setSeverity] = useState<"critical" | "high" | "medium" | "low">("medium");
  const [actualBehavior, setActualBehavior] = useState("");
  const [title, setTitle] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Auto-generate title from component + actual behavior keywords
  useEffect(() => {
    if (actualBehavior.length > 10 && checklistItem) {
      const keywords = extractKeywords(actualBehavior);
      const generatedTitle = `${checklistItem.component}: ${keywords}`;
      setTitle(generatedTitle);
    }
  }, [actualBehavior, checklistItem]);

  const createIssueMutation = useMutation({
    mutationFn: async () => {
      if (!checklistItem || !checklist) throw new Error("No checklist item selected");

      // Step 1: Create the issue first (without screenshot)
      const issueResponse = await apiRequest(
        "POST",
        `/api/testing/checklist-items/${checklistItem.id}/report-issue`,
        {
          severity,
          actualBehavior,
          title: title || `${checklistItem.component}: Issue`,
        }
      );

      const issue = await issueResponse.json();

      // Step 2: If screenshot exists, upload it
      if (screenshot && issue.id) {
        try {
          setIsUploading(true);

          const formData = new FormData();
          formData.append("file", screenshot);
          formData.append("captureType", "manual_upload");
          formData.append("linkedTestId", checklistItem.testId || "");
          formData.append("description", "Screenshot uploaded during issue creation");

          const authHeaders = await getAuthHeaders();
          const uploadResponse = await fetch(`/api/crm/issues/${issue.id}/screenshots/upload`, {
            method: "POST",
            headers: authHeaders,
            credentials: "include",
            body: formData,
          });

          if (!uploadResponse.ok) {
            const error = await uploadResponse.json();
            throw new Error(error.error || "Screenshot upload failed");
          }

          setIsUploading(false);
        } catch (uploadError: any) {
          setIsUploading(false);
          // Log error but don't fail issue creation
          console.error("Screenshot upload error:", uploadError);
          toast({
            title: "Issue created, but screenshot upload failed",
            description: uploadError.message,
            variant: "destructive",
          });
        }
      }

      return issue;
    },
    onSuccess: (data: any) => {
      const message = screenshot
        ? `Issue ${data.issueNumber} created with screenshot attached.`
        : `Issue ${data.issueNumber} has been created and linked to this test step.`;

      toast({
        title: "Issue created successfully",
        description: message,
      });
      // Invalidate all related queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["/api/crm/issues"] });
      queryClient.invalidateQueries({ queryKey: ["/api/testing/checklists"] });
      // FIX: Invalidate the specific checklist issues query to show linked issues immediately
      if (checklist?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/testing/checklists/${checklist.id}/issues`] });
        // CRITICAL: Also invalidate the specific checklist detail to update issuesFound counter
        queryClient.invalidateQueries({ queryKey: [`/api/testing/checklists/${checklist.id}`] });
      }
      onOpenChange(false);
      // Reset form
      setActualBehavior("");
      setTitle("");
      setSeverity("medium");
      setScreenshot(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create issue",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!checklistItem || !checklist) return;
    if (actualBehavior.length < 10) {
      toast({
        title: "Validation error",
        description: "Actual behavior must be at least 10 characters",
        variant: "destructive",
      });
      return;
    }
    createIssueMutation.mutate();
  };

  if (!checklistItem || !checklist) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="issue-report-description">
        <DialogHeader>
          <DialogTitle>Report Issue from Test Step</DialogTitle>
          <DialogDescription id="issue-report-description">
            Create a new issue from test step {checklistItem.testId}. Pre-filled with test context, you only need to provide severity and actual behavior.
          </DialogDescription>
        </DialogHeader>

        {/* Auto-Filled Context (Read-Only Display) */}
        <Card className="bg-muted/50">
          <CardContent className="pt-4 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <strong>Test:</strong> {checklistItem.testId}{" "}
                {checklistItem.stepNumber && `Step ${checklistItem.stepNumber}`}
              </div>
              <div>
                <strong>Sprint:</strong> {checklist.sprintId}
              </div>
              <div>
                <strong>Component:</strong> {checklistItem.component}
              </div>
              <div>
                <strong>Module:</strong>{" "}
                {(checklistItem.component && COMPONENT_TO_MODULE_MAP[checklistItem.component]) || "Unknown"}
              </div>
            </div>
            <div className="text-sm">
              <strong>Expected:</strong> {checklistItem.expectedResult}
            </div>
          </CardContent>
        </Card>

        {/* User Input Fields */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="severity">
              Severity <span className="text-destructive">*</span>
            </Label>
            <Select
              value={severity}
              onValueChange={(val: any) => setSeverity(val)}
            >
              <SelectTrigger id="severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="critical">
                  🔴 P0 - Critical (System Down)
                </SelectItem>
                <SelectItem value="high">
                  🟠 P1 - High (Major Functionality)
                </SelectItem>
                <SelectItem value="medium">
                  🟡 P2 - Medium (Minor Issue)
                </SelectItem>
                <SelectItem value="low">
                  🟢 P3 - Low (Polish/Enhancement)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="actualBehavior">
              Actual Behavior <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="actualBehavior"
              value={actualBehavior}
              onChange={(e) => setActualBehavior(e.target.value)}
              placeholder="Describe what actually happened (minimum 10 characters)..."
              className="min-h-[100px]"
              required
            />
            {actualBehavior.length > 0 && actualBehavior.length < 10 && (
              <p className="text-sm text-destructive mt-1">
                Minimum 10 characters required
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="title">Issue Title (auto-generated)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Auto-generated from component + keywords"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Title is auto-generated from your description. You can edit it if needed.
            </p>
          </div>

          <div>
            <ScreenshotUploadField
              value={screenshot}
              onChange={(files) => setScreenshot(Array.isArray(files) ? files[0] ?? null : files)}
              label="Screenshot (Optional)"
              required={false}
              disabled={createIssueMutation.isPending || isUploading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createIssueMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !severity || actualBehavior.length < 10 || createIssueMutation.isPending || isUploading
            }
          >
            {isUploading
              ? "Uploading screenshot..."
              : createIssueMutation.isPending
              ? "Creating issue..."
              : "Create Issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
